'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import moment from 'moment'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'

// walrus
import { accessibilityLabel } from '@components/kit/_support'
import Box from '@components/kit/box'
import Loading from '@components/kit/loading'
import Page from '@components/kit/page'
import Screen from '@components/kit/screen'
import ScrollView, { ScrollViewNode } from '@components/kit/scroll-view'

// lib
import { ChatContext } from './chat.web'
import SymptomLogHistoryContext, {
  SymptomLogHistoryContextProvider
} from './symptom-log-history-context'
import SymptomLogHistoryDay from './symptom-log-history-day'
import SymptomLogHistoryHeader from './symptom-log-history-header'
import {
  Month,
  Severity,
  SymptomDayLog,
  SymptomLogHistoryDate,
  SymptomLogHistorySection
} from './symptom-log-history-types'
import { useSymptomsStore } from '@store/Symptoms'
import { ISymptomTrackingEntry } from '@store/Symptoms/types'

/* -----------------------------------------------------------------------------
 * helpers
 * -------------------------------------------------------------------------- */

const HEADER_HEIGHT = 80

const countLogsForSection = (section: SymptomLogHistorySection) =>
  section.data.reduce((acc, curr) => acc + curr.logs.length, 0)

const toDayLog = (entry: ISymptomTrackingEntry): SymptomDayLog => ({
  date: entry.date,
  log_id: entry.id,
  severity: entry.severity as Severity,
  symptom_id: entry.symptom.id,
  symptom: entry.symptom.name
})

/**
 * The tracking endpoint returns a flat list ordered by date descending. The
 * history screen renders it grouped by month, then by day.
 */
const toSections = (
  entries: ISymptomTrackingEntry[]
): SymptomLogHistorySection[] => {
  const byMonth = new Map<string, Map<string, SymptomDayLog[]>>()

  entries.forEach(entry => {
    const month = moment.utc(entry.date).format('MM')
    const days = byMonth.get(month) ?? new Map<string, SymptomDayLog[]>()
    const logs = days.get(entry.date) ?? []
    logs.push(toDayLog(entry))
    days.set(entry.date, logs)
    byMonth.set(month, days)
  })

  return [...byMonth.entries()].map(([month, days]) => ({
    month: month as Month,
    data: [...days.entries()].map<SymptomLogHistoryDate>(([date, logs]) => ({
      date,
      logs
    }))
  }))
}

/* -----------------------------------------------------------------------------
 * SymptomLogHistorySectionSeparator
 * -------------------------------------------------------------------------- */

const SymptomLogHistorySectionSeparator = () => <Box bg='shade02' pt='08' />

/* -----------------------------------------------------------------------------
 * SymptomLogHistoryItemSeparator
 * -------------------------------------------------------------------------- */

const SymptomLogHistoryItemSeparator = () => (
  <Box px='24' width='100%' height={1}>
    <Box bg='shade02' flex='1 1 auto' />
  </Box>
)

/* -----------------------------------------------------------------------------
 * SymptomLogHistorySections
 * -------------------------------------------------------------------------- */

type SymptomLogHistorySectionsProps = {
  sections: SymptomLogHistorySection[]
  isLoaded: boolean
}

const SymptomLogHistorySections = ({
  sections,
  isLoaded
}: SymptomLogHistorySectionsProps) => {
  const { headerHeight, headerState, setHeaderState } = useContext(
    SymptomLogHistoryContext
  )
  const scrollRef = useRef<ScrollViewNode | null>(null)

  useEffect(() => {
    if (!sections[0]) {
      return setHeaderState(null)
    }

    const firstDate = sections[0].data?.[0]?.date
    setHeaderState({
      date: firstDate
        ? moment(firstDate, 'YYYY-MM-DD')
        : moment(sections[0].month, 'MM'),
      logCount: countLogsForSection(sections[0])
    })
  }, [sections])

  if (!isLoaded) {
    return (
      <Box flex='1 1 auto' justifyContent='center' alignItems='center'>
        <Loading />
      </Box>
    )
  }

  // the day at the top of the list sets the header's month, as the section
  // list's viewable items did
  const days = sections.flatMap(section =>
    section.data.map(item => ({ date: item.date, section }))
  )
  const handleScroll = () => {
    const scroller = scrollRef.current
    if (!scroller) return
    const top = scroller.getBoundingClientRect().top
    const dayNodes = Array.from(scroller.getInnerViewNode()?.children ?? [])
    const topDay =
      days[
        dayNodes.findIndex(node => node.getBoundingClientRect().bottom > top)
      ]
    if (
      topDay &&
      (!headerState ||
        !moment.utc(topDay.date).isSame(headerState.date, 'month'))
    ) {
      setHeaderState({
        date: moment.utc(topDay.date),
        logCount: countLogsForSection(topDay.section)
      })
    }
  }

  return (
    <Box
      position='absolute'
      top={0}
      right={0}
      bottom={0}
      left={0}
      pt={headerHeight as any}
      pb='48'
    >
      <ScrollView
        ref={scrollRef}
        scrollEventThrottle={50}
        onScroll={handleScroll}
      >
        {sections.map((section, sectionIndex) =>
          section.data.map((item, index) => {
            const isLastInSection = index === section.data.length - 1
            const isLastInList =
              isLastInSection && sectionIndex === sections.length - 1
            // the section list put its section separator after each section's
            // last day, and the screen's separator drew only when there was no
            // section before it: a band after the first month, lines between days
            const separator = isLastInSection ? (
              sectionIndex === 0 ? (
                <SymptomLogHistorySectionSeparator />
              ) : null
            ) : !isLastInList ? (
              <SymptomLogHistoryItemSeparator />
            ) : null
            return (
              <Box key={`${sectionIndex}:${item.date}`}>
                <SymptomLogHistoryDay
                  date={moment.utc(item.date)}
                  logs={item.logs}
                />
                {separator}
              </Box>
            )
          })
        )}
      </ScrollView>
    </Box>
  )
}

/* -----------------------------------------------------------------------------
 * SymptomLogHistory
 * -------------------------------------------------------------------------- */

function SymptomLogHistory() {
  const { userId, selectedLogGroup } = useContext(ChatContext)
  const { getAllSymptomTracking } = useSymptomsStore.getState()
  const [entries, setEntries] = useState<ISymptomTrackingEntry[]>([])
  const [isLoaded, setIsLoaded] = useState<boolean>(false)

  useEffect(() => {
    let current = true
    setIsLoaded(false)
    getAllSymptomTracking(userId).then(tracking => {
      if (current) {
        setEntries(tracking)
        setIsLoaded(true)
      }
    })
    return () => {
      current = false
    }
  }, [getAllSymptomTracking, userId])

  // a log group is now a symptom, so the selection filters by symptom id
  const sections = useMemo(
    () =>
      toSections(
        selectedLogGroup
          ? entries.filter(entry => entry.symptom.id === selectedLogGroup)
          : entries
      ),
    [entries, selectedLogGroup]
  )

  return (
    <SymptomLogHistoryContextProvider headerHeight={HEADER_HEIGHT}>
      <Screen {...accessibilityLabel('Symptom Log History')}>
        <Page p='00'>
          <React.Fragment>
            <SymptomLogHistoryHeader />
            <SymptomLogHistorySections
              sections={sections}
              isLoaded={isLoaded}
            />
          </React.Fragment>
        </Page>
      </Screen>
    </SymptomLogHistoryContextProvider>
  )
}

export default SymptomLogHistory
